import { read_timeline_value_label } from "../../../utils/timeline/timeline_formatting";
import { read_timeline_plain_text } from "../../../utils/timeline/timeline_normalization";
import {
    read_seller_member,
    read_seller_name
} from "../../../utils/hr/seller_identity";

const salesSessionEventTypeLookup = {
    COMMENT: "COMMENT",
    SASS_HANDOFF: "SASS",
    SASS: "SASS",
    SET: "SASS_SET",
    SASS_SET: "SASS_SET",
    SASS_RESCHEDULE: "SASS_RESCHEDULE",
    IN_PROGRESS: "IN_PROGRESS",
    SASS_IN_PROGRESS: "SASS_IN_PROGRESS",
    LOST: "LOST",
    SASS_LOST: "SASS_LOST",
    PAID: "SASS_PAID",
    SASS_PAID: "SASS_PAID"
};

const generatedSalesSessionTextLookup = {
    IN_PROGRESS: /^in progress$/i,
    LOST: /^lost$/i,
    SASS: /^sass (meeting|handoff|scheduled)\b/i,
    SASS_SET: /^sass (set|scheduled)\b/i,
    SASS_RESCHEDULE: /^sass rescheduled\b/i,
    SASS_IN_PROGRESS: /^sass.*in progress\b/i,
    SASS_LOST: /^sass.*lost\b/i,
    SASS_PAID: /^sass.*paid\b|^sass payment\b/i
};

const salesOutcomeAliasLookup = {
    agency: "Agency",
    "contract sent": "Agency",
    sass: "Sass",
    lost: "Lost",
    update: "Update"
};

const hiddenSalesChangeFields = [
    "status",
    "schedule",
    "meetingdate",
    "callbackat",
    "closedat",
    "outcome",
    "seller",
    "registrationstatus",
    "registration_status",
    "draftofrid",
    "draftofferid",
    "ofrid",
    "offerid",
    "sourceofferid"
];

// Maps historical aliases to the nested Sales-session event contract.
export const read_sales_session_event_type = (event) => {
    const rawType = `${event?.type || ""}`.toUpperCase();

    return salesSessionEventTypeLookup[rawType] || rawType;
};

// Converts compact tuples and full stored field changes into one readable shape.
export const read_sales_session_change = (change) => {
    const changeReaders = {
        true: () => ({ field: change?.[0], from: change?.[1], to: change?.[2] }),
        false: () => change || {}
    };

    return changeReaders[Array.isArray(change)]();
};

// Removes generated status text while retaining genuine comments.
export const read_sales_session_event_text = (event) => {
    const type = read_sales_session_event_type(event);
    const value = event?.text || event?.note || event?.data?.comment || event?.comment || "";
    const eventText = read_timeline_plain_text(value);

    return generatedSalesSessionTextLookup[type]?.test(eventText) ? "" : eventText;
};

// Allows only outcomes that can genuinely complete a Sales session.
export const read_sales_session_outcome = (event) => {
    const savedOutcome = event?.data?.outcome || event?.to?.outcome || event?.to?.status;

    return salesOutcomeAliasLookup[`${savedOutcome || ""}`.trim().toLowerCase()] || "Update";
};

const read_sales_seller_display_name = (resolvedSeller = {}, savedSeller = {}) => {
    const savedIdentity = [
        read_seller_name(resolvedSeller),
        read_seller_name(savedSeller),
        resolvedSeller?.email,
        resolvedSeller?.username,
        savedSeller?.email,
        savedSeller?.username
    ].map((value) => `${value || ""}`.trim().toLowerCase());
    const isEvaSertaridou = savedIdentity.includes("eua sertaridou")
        || savedIdentity.some((value) => /^e\.sertaridou@doitforme\.(eu|us)$/.test(value));

    return isEvaSertaridou
        ? "Eva Sertaridou"
        : read_seller_name(resolvedSeller) || read_seller_name(savedSeller) || "Unknown seller";
};

// Resolves the seller snapshot retained on one completed Sales session.
export const read_sales_session_seller = (event, teamMembers = []) => {
    const savedSeller = event?.data?.seller || event?.detail?.seller || event?.from?.seller || event?.actor || {};
    const seller = {
        ...savedSeller,
        name: savedSeller?.name || savedSeller?.label || savedSeller?.displayName || ""
    };
    const resolvedSeller = read_seller_member(seller, teamMembers) || seller;

    return {
        name: read_sales_seller_display_name(resolvedSeller, seller)
    };
};

// Reads the exact status movement retained on one nested audit event.
export const read_sales_status_change = (event) => {
    const statusChange = (event?.changes || event?.data?.changes || []).map(read_sales_session_change).find((change) => {
        return `${change?.field || ""}`.toLowerCase() === "status";
    });
    const previousStatus = statusChange?.from ?? event?.detail?.previousStatus ?? event?.data?.previousStatus ?? event?.from?.status;
    const exitOutcome = read_sales_session_event_type(event) === "SALES_EXIT"
        ? read_sales_session_outcome(event)
        : "";
    const currentStatus = exitOutcome || statusChange?.to || event?.detail?.status || event?.data?.nextStatus || event?.to?.status;

    if (!currentStatus || `${previousStatus || ""}` === `${currentStatus || ""}`) {
        return null;
    }

    return {
        from: read_timeline_value_label(previousStatus),
        to: read_timeline_value_label(currentStatus)
    };
};

// Reads the callback or meeting time from current and historical event shapes.
export const read_sales_schedule = (event) => {
    const scheduleChange = (event?.changes || event?.data?.changes || []).map(read_sales_session_change).find((change) => {
        return ["schedule", "meetingdate", "callbackat"].includes(`${change?.field || ""}`.toLowerCase());
    });

    return event?.detail?.schedule
        || event?.data?.schedule
        || event?.data?.rescheduledAt
        || event?.to?.schedule
        || event?.to?.meetingDate
        || scheduleChange?.to
        || "";
};

// Reads field edits while leaving status and scheduling values to their scenarios.
export const read_sales_field_changes = (event) => {
    return (event?.changes || event?.data?.changes || []).map(read_sales_session_change).filter((change) => {
        return !hiddenSalesChangeFields.includes(`${change?.field || ""}`.toLowerCase());
    });
};

const read_sales_event_key = (event, index) => {
    return event?.id || event?.sourceKey || event?._id || `sales-event-${index}`;
};

export const read_sales_event_time = (event) => {
    return new Date(event?.at || event?.changedAt || 0).getTime() || 0;
};

const read_sales_text_key = (event) => {
    return read_sales_session_event_text(event).toLowerCase().replace(/\s+/g, " ").trim();
};

// Retains meaningful nested evidence in chronological order.
export const read_sales_session_evidence = (event) => {
    return (event?.data?.events || []).filter((sessionEvent) => {
        const type = read_sales_session_event_type(sessionEvent);

        return `${sessionEvent?.type || ""}`.toLowerCase() !== "sass_exit"
            && (type !== "COMMENT" || Boolean(read_sales_text_key(sessionEvent)));
    }).map((sessionEvent, index) => ({
        ...sessionEvent,
        salesEventKey: read_sales_event_key(sessionEvent, index)
    })).sort((firstEvent, secondEvent) => {
        return read_sales_event_time(firstEvent) - read_sales_event_time(secondEvent)
            || firstEvent.salesEventKey.localeCompare(secondEvent.salesEventKey);
    });
};

// Combines Stalling movement, callback and matching comment into one scenario.
const read_combined_callback_events = (events) => {
    const consumedEventKeys = new Set();
    const combinedEvents = new Map();

    events.filter((event) => read_sales_schedule(event)).forEach((callbackEvent) => {
        const callbackTime = read_sales_event_time(callbackEvent);
        const callbackTextKey = read_sales_text_key(callbackEvent);
        const nearbyEvents = events.filter((candidate) => {
            const timeDifference = Math.abs(read_sales_event_time(candidate) - callbackTime);

            return candidate.salesEventKey !== callbackEvent.salesEventKey && timeDifference <= 300000;
        });
        const statusEvent = [...nearbyEvents].reverse().find((candidate) => {
            return read_sales_status_change(candidate)?.to === "Stalling" && !read_sales_schedule(candidate);
        });
        const commentEvent = nearbyEvents.find((candidate) => {
            return read_sales_session_event_type(candidate) === "COMMENT"
                && callbackTextKey
                && read_sales_text_key(candidate) === callbackTextKey;
        });

        [statusEvent, commentEvent].filter(Boolean).forEach((consumedEvent) => {
            consumedEventKeys.add(consumedEvent.salesEventKey);
        });

        combinedEvents.set(callbackEvent.salesEventKey, {
            ...callbackEvent,
            salesStatusChange: read_sales_status_change(statusEvent) || read_sales_status_change(callbackEvent),
            salesComment: read_sales_session_event_text(commentEvent) || read_sales_session_event_text(callbackEvent)
        });
    });

    return { consumedEventKeys, combinedEvents };
};

// Removes supporting evidence that is already represented by the Sales card.
const read_visible_sales_events = (events) => {
    const { consumedEventKeys, combinedEvents } = read_combined_callback_events(events);

    return events.filter((event) => {
        const type = read_sales_session_event_type(event);
        const statusChange = read_sales_status_change(event);
        const hiddenSellerEvent = ["SELLER", "SELLER_ASSIGNED", "SELLER_REMOVED"].includes(type);
        const hiddenInitialToCall = statusChange?.to === "To Call"
            && (!statusChange?.from || statusChange.from === "Backlog");
        const hiddenSassUpdate = `${statusChange?.from || ""}`.trim().toLowerCase() === "sass"
            && `${statusChange?.to || ""}`.trim().toLowerCase() === "update"
            && type !== "COMMENT"
            && !read_sales_schedule(event)
            && !read_sales_field_changes(event).length;

        return !consumedEventKeys.has(event.salesEventKey)
            && !hiddenSellerEvent
            && !hiddenInitialToCall
            && !hiddenSassUpdate
            && (type === "COMMENT" || read_sales_schedule(event) || statusChange || read_sales_field_changes(event).length);
    }).map((event) => combinedEvents.get(event.salesEventKey) || event);
};

// Selects the renderer scenario for one visible Sales-session event.
const read_sales_event_scenario = (event) => {
    const type = read_sales_session_event_type(event);

    if (read_sales_schedule(event)) {
        return "callback";
    }

    if (type === "COMMENT") {
        return "comment";
    }

    if (read_sales_status_change(event)) {
        return "status";
    }

    return "edit";
};

// Builds the small view contract consumed by the four Sales scenario components.
const read_sales_event_view = (event, sessionSeller) => {
    return {
        key: event.salesEventKey,
        at: read_sales_event_time(event),
        scenario: read_sales_event_scenario(event),
        statusChange: event.salesStatusChange || read_sales_status_change(event),
        schedule: read_sales_schedule(event),
        comment: event.salesComment || read_sales_session_event_text(event),
        fieldChanges: read_sales_field_changes(event),
        sessionSeller
    };
};

// Returns the documented, visible scenarios for one completed Sales session.
export const read_sales_session_events = (event) => {
    if (read_sales_session_outcome(event) === "Agency") {
        return [];
    }

    const sessionSeller = event?.data?.seller || event?.detail?.seller || event?.from?.seller || event?.actor || null;

    return read_visible_sales_events(read_sales_session_evidence(event)).map((sessionEvent) => {
        return read_sales_event_view(sessionEvent, sessionSeller);
    });
};
